import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  RefreshCcw,
  Save,
  CalendarDays,
  CheckSquare,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

// ---------- helpers ----------
const toDDMMYYYY = (isoDate) => {
  // isoDate: "yyyy-mm-dd" -> "dd/MM/yyyy"
  if (!isoDate) return "";
  const [y, m, d] = String(isoDate).split("-");
  if (!y || !m || !d) return "";
  return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
};

const toISODate = (ddmmyyyy) => {
  // "dd/MM/yyyy" -> "yyyy-mm-dd"
  if (!ddmmyyyy) return "";
  const [d, m, y] = String(ddmmyyyy).split("/");
  if (!y || !m || !d) return "";
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const isFutureISODate = (isoDate) => {
  if (!isoDate) return false;
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return d.getTime() > today.getTime();
};

const RejoiningResignedEmployee = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // you told this is default after login
  const branchType = localStorage.getItem("branchType") || "HO";
  const loginuserid = localStorage.getItem("userId") || localStorage.getItem("loginUserId") || "";

  // meta
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);

  // filters
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState(""); // dropdown
  const [deptId, setDeptId] = useState("");
  const [desigId, setDesigId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [pfNonPf, setPfNonPf] = useState("Select");
  const [payMode, setPayMode] = useState("Select");

  // autocomplete
  const [empCodeSug, setEmpCodeSug] = useState([]);
  const [empNameSug, setEmpNameSug] = useState([]);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // per-row inputs
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]); // checked rows (employeeId)
  const [rejoinISOByEmpId, setRejoinISOByEmpId] = useState({}); // { [employeeId]: "yyyy-mm-dd" }

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-24", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const disabledBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg bg-gray-400 cursor-not-allowed`;

  const axiosHeaders = () => ({
    "x-loginuserid": loginuserid || "",
  });

  // ---------- API calls ----------
  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim()) {
      toast.warn("BranchId not found in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/rejoining-resigned-employee/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType || "HO"),
        },
        headers: axiosHeaders(),
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);
      setDepartments(res.data.departments || []);
      setDesignations(res.data.designations || []);
      setGrades(res.data.grades || []);

      // default branch selection like ASP
      setSelectedBranchId(String(res.data.defaultBranchId || loginBranchId || ""));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim()) {
      toast.warn("BranchId not found in localStorage.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/payroll/rejoining-resigned-employee/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId), // login branch id
          branchType: String(branchType || "HO"),

          selectedBranchId: selectedBranchId || "Select",
          empCode: empCode.trim(),
          empName: empName.trim(),
          deptId: deptId || "Select",
          desigId: desigId || "Select",
          gradeId: gradeId || "Select",
          pfNonPf: pfNonPf || "Select",
          payMode: payMode || "Select",

          page: p,
          pageSize,
        },
        headers: axiosHeaders(),
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "List load failed");
        return;
      }

      const newRows = res.data.rows || [];
      setRows(newRows);
      setTotal(res.data.total || 0);
      setPage(res.data.page || p);

      const tp = Math.max(1, Math.ceil((res.data.total || 0) / pageSize));
      setTotalPages(tp);

      // reset selections on new fetch (same pattern as your template)
      setSelectedEmployeeIds([]);
      setRejoinISOByEmpId({});
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed");
    } finally {
      setLoading(false);
    }
  };

  // ----- autocomplete (debounced) -----
  useEffect(() => {
    const t = setTimeout(async () => {
      const prefix = empCode.trim();
      if (!prefix || prefix.length < 1) {
        setEmpCodeSug([]);
        return;
      }
      if (!String(finYearId).trim() || !String(loginBranchId).trim()) return;

      try {
        const res = await axios.get(`${BASE_URL}/api/payroll/rejoining-resigned-employee/autocomplete/empcode`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(selectedBranchId || loginBranchId),
            branchType: String(branchType || "HO"),
            prefix,
          },
          headers: axiosHeaders(),
        });
        if (res?.data?.success) setEmpCodeSug(res.data.items || []);
      } catch {
        // silent (autocomplete)
      }
    }, 350);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode, finYearId, loginBranchId, selectedBranchId, branchType]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const prefix = empName.trim();
      if (!prefix || prefix.length < 1) {
        setEmpNameSug([]);
        return;
      }
      if (!String(finYearId).trim() || !String(loginBranchId).trim()) return;

      try {
        const res = await axios.get(`${BASE_URL}/api/payroll/rejoining-resigned-employee/autocomplete/empname`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(selectedBranchId || loginBranchId),
            branchType: String(branchType || "HO"),
            prefix,
          },
          headers: axiosHeaders(),
        });
        if (res?.data?.success) setEmpNameSug(res.data.items || []);
      } catch {
        // silent
      }
    }, 350);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName, finYearId, loginBranchId, selectedBranchId, branchType]);

  // initial load
  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // page change fetch
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ---------- actions ----------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await loadMeta();
    setPage(1);
    await fetchList(1);
  };

  const toggleSelect = (employeeId) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(employeeId) ? prev.filter((x) => x !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (!rows.length) return;
    const allIds = rows.map((r) => Number(r.fld_employeeid || r.fld_Employeeid || r.fld_EmployeeID || r.employeeId));
    const allChecked = allIds.every((id) => selectedEmployeeIds.includes(id));

    if (allChecked) {
      setSelectedEmployeeIds([]);
      return;
    }
    setSelectedEmployeeIds(allIds);
  };

  const setRejoinDateISO = (employeeId, iso) => {
    setRejoinISOByEmpId((prev) => ({ ...prev, [employeeId]: iso }));
  };

  const handleRejoinSubmit = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (selectedEmployeeIds.length === 0) {
      toast.warn("Please Select Any One");
      return;
    }

    // ASP validates each checked row must have rejoin date
    for (const empId of selectedEmployeeIds) {
      const iso = rejoinISOByEmpId[empId];
      if (!iso) {
        toast.warn("Please Select Rejoin Date");
        return;
      }
      if (isFutureISODate(iso)) {
        toast.warn("You can select only Previous days");
        return;
      }
    }

    const ok = window.confirm("Are You Sure To Submit?");
    if (!ok) return;

    try {
      setLoading(true);

      const items = selectedEmployeeIds.map((empId) => ({
        employeeId: empId,
        rejoinDate: toDDMMYYYY(rejoinISOByEmpId[empId]),
      }));

      const res = await axios.post(
        `${BASE_URL}/api/payroll/rejoining-resigned-employee/rejoin`,
        { finYearId: Number(finYearId), items },
        { headers: axiosHeaders() }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Rejoined Successfully..");
        setPage(1);
        await fetchList(1);
      } else {
        toast.error(res?.data?.message || "Submit failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">RESIGNED EMPLOYEE REJOINING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Resigned employee rejoining
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Pagination / Rejoin Submit
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button
                onClick={handleRejoinSubmit}
                className={selectedEmployeeIds.length ? primaryBtn : disabledBtn}
                disabled={loading || selectedEmployeeIds.length === 0}
                title={selectedEmployeeIds.length === 0 ? "Select at least one employee" : "Submit Rejoin"}
              >
                <Save className="w-4 h-4" />
                Rejoin Submit
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Employee Code (autocomplete) */}
              <div className="lg:col-span-2">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2">
                  <input
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    placeholder="Type employee code..."
                    className={inputBase}
                    list="empcode-suggestions"
                  />
                  <datalist id="empcode-suggestions">
                    {(empCodeSug || []).slice(0, 15).map((x, i) => (
                      <option value={x} key={i} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Employee Name (autocomplete) */}
              <div className="lg:col-span-2">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2">
                  <input
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    placeholder="Type employee name..."
                    className={inputBase}
                    list="empname-suggestions"
                  />
                  <datalist id="empname-suggestions">
                    {(empNameSug || []).slice(0, 15).map((x, i) => (
                      <option value={x} key={i} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <select
                  className={`${inputBase} w-full`}
                  value={selectedBranchId}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                >
                  <option value="">Select</option>
                  {branches.map((b) => (
                    <option key={b.fld_branchid} value={b.fld_branchid}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>Department</label>
                <select className={`${inputBase} w-full`} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                  <option value="">Select</option>
                  {departments.map((d) => (
                    <option key={d.fld_deptid} value={d.fld_deptid}>
                      {d.fld_departmentname}
                    </option>
                  ))}
                </select>
              </div>

              {/* Grade */}
              <div>
                <label className={labelBase}>Grade</label>
                <select className={`${inputBase} w-full`} value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
                  <option value="">Select</option>
                  {grades.map((g) => (
                    <option key={g.fld_gradeid} value={g.fld_gradeid}>
                      {g.fld_gradename}
                    </option>
                  ))}
                </select>
              </div>

              {/* Designation */}
              <div>
                <label className={labelBase}>Designation</label>
                <select className={`${inputBase} w-full`} value={desigId} onChange={(e) => setDesigId(e.target.value)}>
                  <option value="">Select</option>
                  {designations.map((d) => (
                    <option key={d.fld_desigid} value={d.fld_desigid}>
                      {d.fld_designationname}
                    </option>
                  ))}
                </select>
              </div>

              {/* PF/Non PF */}
              <div>
                <label className={labelBase}>PF/Non PF</label>
                <select className={`${inputBase} w-full`} value={pfNonPf} onChange={(e) => setPfNonPf(e.target.value)}>
                  <option value="Select">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Payment Mode */}
              <div>
                <label className={labelBase}>Payment Mode</label>
                <select className={`${inputBase} w-full`} value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                  <option value="Select">Select</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Resign Date</th>
                    <th className="px-4 py-4 text-center font-bold">Gross Salary</th>
                    <th className="px-4 py-4 text-center font-bold">Location</th>
                    <th className="px-4 py-4 text-center font-bold">Rejoin Date</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        <CheckSquare className="w-4 h-4" />
                        Select All
                        <input
                          type="checkbox"
                          checked={rows.length > 0 && rows.every((r) => {
                            const empId = Number(r.fld_employeeid ?? r.fld_Employeeid ?? r.fld_EmployeeID ?? r.employeeId);
                            return selectedEmployeeIds.includes(empId);
                          })}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 9 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const employeeId = Number(r.fld_employeeid ?? r.fld_Employeeid ?? r.fld_EmployeeID ?? r.employeeId);
                      const checked = selectedEmployeeIds.includes(employeeId);
                      const iso = rejoinISOByEmpId[employeeId] || "";

                      return (
                        <tr
                          key={`${employeeId}-${idx}`}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                            isDark ? "bg-gray-800" : "bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employeecode}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employee}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_designationname}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.resigndate}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_grosssalary}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_BranchName}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <CalendarDays className={`w-4 h-4 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                              <input
                                type="date"
                                value={iso}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (isFutureISODate(val)) {
                                    toast.warn("You can select only Previous days");
                                    return;
                                  }
                                  setRejoinDateISO(employeeId, val);
                                }}
                                className={`px-4 py-2 rounded-2xl border outline-none ${
                                  isDark
                                    ? "bg-gray-800/40 border-white/10 text-white"
                                    : "bg-white border-gray-200 text-gray-900"
                                }`}
                              />
                            </div>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSelect(employeeId)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* small hint */}
          <div className={`mt-4 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Note: Rejoin date cannot be in future (same as ASP validation).
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default RejoiningResignedEmployee;
